import React, { useState, useEffect } from 'react';
import { Canvas } from '@react-three/fiber';
import { OrbitControls } from '@react-three/drei';
import DroneModel from '../components/DroneModel';
import './LaptopViewer.css';
import { FaWhatsapp } from "react-icons/fa";
import 'bootstrap/dist/css/bootstrap.min.css';
import { Modal, Button, Form } from 'react-bootstrap';
import logo from '../assets/TT logo.png';
import "driver.js/dist/driver.css";
import { driver } from "driver.js";
import { Link } from 'react-router-dom';

// Map raw model part names → user-friendly aliases
const partNameAliases = {
  "Motherboard_&_Hardware_-0": "Motherboard & Hardware",
  "Front_Main_camera_-1": "Front Main Camera",
  "Front_camera_Drive_-_2": "Front Camera Drive",
  "Top_Camera_-_3": "Top Camera",
  "Front_LEFT_drive_-_4": "Front LEFT Drive",
  "Front_RIGHT_drive_-_5": "Front RIGHT Drive",
  "Rear_Right_Drive_-_6": "Rear Right Drive",
  "Rear_Left_Drive_-_7": "Rear Left Drive",
  "Charging_port_-_8": "Charging Port",
  "DATA_port_-9": "DATA Port",
  "Battery_-_10": "Battery"
};

// Issues mapped to ALIAS (not raw names)
const partIssues = {
  "Motherboard & Hardware": [
    "Battery charging slowly",
    "Drone not powering on",
    "Short flight time",
    "Remote not pairing",
    "Compass calibration error",
    "ESC error",
    "Video feed lag",
    "Obstacle sensors malfunction",
    "Transmission module burnt",
    "GPS module failure",
    "Microphone not recording",
    "SD card not detected",
    "Image stabilization not working",
    "Wi-Fi not connecting",
    "Random freezing",
    "Water damage",
    "Camera sensor failure",
    "Main board failure",
    "Charging IC burnt"
  ],
  "Front Main Camera": [
    "Camera blank images",
    "No gimbal moments",
    "No tilt & pan control",
    "Not stabilizing",
    "Distortion color output"
  ],
  "Front Camera Drive": [
    "Distortion color output",
    "Camera blank images"
  ],
  "Top Camera": [
    "Distortion color output",
    "Camera blank images"
  ],
  "Front LEFT Drive": [
    "Gimbal motor overload",
    "Vibration",
    "Not spinning"
  ],
  "Front RIGHT Drive": [
    "Gimbal motor overload",
    "Vibration",
    "Not spinning"
  ],
  "Rear Right Drive": [
    "Gimbal motor overload",
    "Vibration",
    "Not spinning"
  ],
  "Rear Left Drive": [
    "Gimbal motor overload",
    "Vibration",
    "Not spinning"
  ],
  "Charging Port": [
    "USB-C port loose",
    "No charging light"
  ],
  "DATA Port": [
    "Not detecting / not reading"
  ],
  "Battery": [
    "Battery not recognized",
    "Main board failure",
    "Charging IC burnt"
  ]
};


const DroneCamera = () => {
  const [selectedPart, setSelectedPart] = useState(null); // RAW name
  const [hoveredPart, setHoveredPart] = useState(null);   // RAW name
  const [modalShow, setModalShow] = useState(false);
  const [modalContent, setModalContent] = useState('');
  const [formData, setFormData] = useState({ name: '', email: '', phone: '' });

  // Convert raw name → alias for UI
  const getAlias = (rawName) => partNameAliases[rawName] || rawName;

  const hoveredAlias = getAlias(hoveredPart);
  const selectedAlias = getAlias(selectedPart);

  const initialCameraPosition = [0, 0.5, 0.5];

  // Store RAW name for highlight logic
  const handlePartClick = (rawName) => {
    setSelectedPart(rawName);
  };

  const handleIssueClick = (issue) => {
    setModalContent(issue);
    setModalShow(true);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

const handleSubmit = () => {
    const message = `Hello, I want to report an issue:
- Device: Nintendo Switch
- Part Name: ${selectedAlias || "Not Selected"}
- Issue: ${modalContent}
- Name: ${formData.name}
- Email: ${formData.email}
- Phone: ${formData.phone}
- Location: ${formData.location}
- Issue Description: ${formData.issue}`;

    const encodedMessage = encodeURIComponent(message);
    const whatsappNumber = "971567756193"; // ✅ replace with your WhatsApp number

    // Works on both WhatsApp mobile app & WhatsApp Web (desktop)
    const url = `https://api.whatsapp.com/send?phone=${whatsappNumber}&text=${encodedMessage}`;

    window.open(url, "_blank");

    setModalShow(false);
    setFormData({ name: "", email: "", phone: "" });
  };

  // ✅ Guided tour with driver.js
  const startTour = () => {
    const tourDriver = driver({
      showProgress: true,
      showButtons: ["next", "previous", "close"],
      steps: [
        {
          element: ".model-canvas",
          popover: {
            title: "3D Laptop Model",
            description: "👉 Rotate and zoom the product with your mouse or touchpad.",
          },
        },
        {
          element: ".sidebar",
          popover: {
            title: "Parts List",
            description: "Click a part from this list or directly on the product model.",
          },
        },
        {
          element: ".custom-modal",
          popover: {
            title: "Submit an Issue",
            description: "Choose an issue and send it via WhatsApp for quick support!",
          },
        },
      ],
    });

    tourDriver.drive();
  };

  useEffect(() => {
    const hasSeenTour = localStorage.getItem("hasSeenTour");
    if (!hasSeenTour) {
      startTour();
      localStorage.setItem("hasSeenTour", "true");
    }
  }, []);

  return (
    <>
    <header className="top-header d-flex justify-content-between align-items-center px-3 py-2">
            <Link to={'/'}><img src={logo} style={{height: '160px', margin: '-40px -30px', padding: '0px'}} /></Link>
            <Button variant="info" onClick={startTour}>
              📖 Show Instructions
            </Button>
          </header>
      <div className="laptop-viewer-container">
        <div className="model-canvas">
          <Canvas camera={{ position: initialCameraPosition, fov: 50 }}>
            <ambientLight />
            <directionalLight position={[10, 10, 5]} />
            <DroneModel
              onPartClick={handlePartClick}
              onHover={setHoveredPart}
              selectedPart={selectedPart} // RAW name
            />
            <OrbitControls />
          </Canvas>
          {hoveredPart && <div className="tooltip">{hoveredAlias}</div>}
        </div>

        <div className="sidebar">
          {!selectedPart && (
            <>
              <h3>All Parts</h3>
              <ul>
                {Object.entries(partNameAliases).map(([rawName, alias], i) => (
                  <li
                    key={i}
                    onClick={() => handlePartClick(rawName)} // RAW name
                    style={{ cursor: 'pointer' }}
                  >
                    {alias}
                  </li>
                ))}
              </ul>
            </>
          )}

          {selectedPart && (
            <>
              <h3>{selectedAlias} Issues</h3>
              <ul>
                {(partIssues[selectedAlias] || []).map((issue, i) => (
                  <li
                    key={i}
                    onClick={() => handleIssueClick(issue)}
                    style={{ cursor: 'pointer' }}
                  >
                    {issue}
                  </li>
                ))}
              </ul>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => setSelectedPart(null)}
              >
                🔙 Back to Parts
              </Button>
            </>
          )}
        </div>
      </div>

      <Modal show={modalShow} onHide={() => setModalShow(false)} centered className="custom-modal">
        <Modal.Header style={{ color: 'white' }} closeButton>
          <Modal.Title>{modalContent}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3" controlId="formName">
              <Form.Label>Name</Form.Label>
              <Form.Control
                type="text"
                placeholder="Enter your name"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
              />
            </Form.Group>
            <Form.Group className="mb-3" controlId="formEmail">
              <Form.Label>Email</Form.Label>
              <Form.Control
                type="email"
                placeholder="Enter email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
              />
            </Form.Group>
            <Form.Group className="mb-3" controlId="formPhone">
              <Form.Label>Phone</Form.Label>
              <Form.Control
                type="tel"
                placeholder="Enter phone number"
                name="phone"
                value={formData.phone}
                onChange={handleInputChange}
              />
            </Form.Group>
            <Form.Group className="mb-3" controlId="formLocation">
              <Form.Label>Location</Form.Label>
              <Form.Control
                type="text"
                placeholder="Enter Location"
                name="location"
                value={formData.location}
                onChange={handleInputChange}
              />
            </Form.Group>
            <Form.Group className="mb-3" controlId="formEmergency">
              <Form.Label>Location</Form.Label>
              <Form.Control
                type="text"
                placeholder="Urgency Level"
                name="Emergency"
                value={formData.Emergency}
                onChange={handleInputChange}
              />
            </Form.Group>
            <Form.Group className="mb-3" controlId="formIssue">
              <Form.Label>Describe Issue</Form.Label>
              <Form.Control
                as="textarea"
                rows={4} // You can adjust the number of rows as needed
                placeholder="Enter Issue Description"
                name="issue"
                value={formData.issue}
                onChange={handleInputChange}
              />
            </Form.Group>

          </Form>
          <Button variant="success" onClick={handleSubmit} className="d-flex align-items-center gap-2">
            <FaWhatsapp size={20} />
            Submit Issue on WhatsApp
          </Button>

        </Modal.Body>
        <Modal.Footer>

        </Modal.Footer>
      </Modal>
    </>
  );
};

export default DroneCamera;
